// Single, low-level auth shim.
//
// Patches window.fetch and XMLHttpRequest ONCE so every browser->backend request carries the JWT:
// React's axios (uses XHR under the hood), React's raw fetch, AND the tool-HTML forms that are
// injected into the SAME window (dangerously-set-html-content, not an iframe). This means no caller
// has to be edited individually.
//
// The token is attached ONLY when the request targets the turtle360 backend (isBackendUrl). Calls
// to external services (Supabase, presolv360, EmailJS, CDNs) are left completely untouched, so the
// turtle360 token is never leaked and their own auth headers are never overwritten.
import { baseUrl } from "./variables/variables";

const AUTH_KEYS = ["login", "userIn", "userId", "userEmail", "token"];

const getToken = () => {
  try {
    return localStorage.getItem("token");
  } catch (e) {
    return null;
  }
};

// True only for turtle360 backend URLs. baseUrl is absolute (e.g. https://turtle.presolv360.com or
// http://localhost:8080) and every backend call is `${baseUrl}${path}`, so a prefix match is exact.
// Relative URLs resolve against the current origin (which equals baseUrl in same-origin staging/prod).
export const isBackendUrl = (rawUrl) => {
  if (!rawUrl) return false;
  try {
    return new URL(rawUrl, window.location.origin).href.startsWith(baseUrl);
  } catch (e) {
    return false;
  }
};

let handlingUnauthorized = false;
const handle401 = () => {
  if (handlingUnauthorized) return; // fire once; avoid redirect loops
  handlingUnauthorized = true;
  try {
    AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    /* ignore */
  }
  if (window.location.pathname !== "/") window.location.replace("/");
};

// --- fetch ---
if (typeof window !== "undefined" && window.fetch) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const url = typeof input === "string" ? input : input && input.url;
    let finalInit = init;
    if (isBackendUrl(url) && getToken()) {
      const headers = new Headers(
        (init && init.headers) ||
          (typeof input !== "string" && input ? input.headers : undefined)
      );
      // Always set for backend URLs — overwrite any pre-existing Authorization (some legacy
      // adapters hardcode an empty `Authorization: "Bearer "`, which would otherwise be sent
      // as-is and 401). External URLs never reach here, so their own auth headers are untouched.
      headers.set("Authorization", "Bearer " + getToken());
      finalInit = { ...(init || {}), headers };
    }
    return originalFetch(input, finalInit).then((response) => {
      if (response && response.status === 401 && isBackendUrl(url)) handle401();
      return response;
    });
  };
}

// --- XMLHttpRequest (axios and the injected tool-HTML forms use this) ---
if (typeof XMLHttpRequest !== "undefined") {
  const proto = XMLHttpRequest.prototype;
  const originalOpen = proto.open;
  const originalSend = proto.send;

  proto.open = function (method, url, ...rest) {
    this.__turtleUrl = url;
    return originalOpen.call(this, method, url, ...rest);
  };

  proto.send = function (body) {
    if (isBackendUrl(this.__turtleUrl) && getToken()) {
      try {
        this.setRequestHeader("Authorization", "Bearer " + getToken());
      } catch (e) {
        /* header already set / invalid state — ignore */
      }
      this.addEventListener("load", function () {
        if (this.status === 401) handle401();
      });
    }
    return originalSend.call(this, body);
  };
}
